import { useAppDisplayModeContext } from '@archipro-design/aria';
import PageHeader from '@modules/project/component/page-header/PageHeader';
import Summary from '@modules/project/component/summary/Summary';
import DetailsMobile from '@modules/project/component/details/Details.mobile';
import ContactsMobile from '@modules/project/component/contacts/Contacts.mobile';
import Awards from '@modules/project/component/awards/Awards';

import DetailBannerSection from '~/modules/page-builder/component/detail/detail-banner-section/DetailBannerSection';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import {
    BANNER_WIDTH_FULL_MOBILE,
    FULL_COLUMN_WIDTH_FULL_SCREEN,
} from '~/modules/page-builder/config/column-constants';
import DownloadAction from '~/modules/page-builder/component/detail/detail-banner-section/actions/DownloadAction';
import { getBannerDimensions } from '~/utils/banner-utils';
import { useProjectPreviewRouteData } from '@modules/project/hook/use-project-preview-route-data';
import { ProjectDetailsAccordion } from '../details-accordion/ProjectDetailsAccordion';

interface ProjectPageHeaderProps {
    canDownloadBannerImage: boolean;
}

const ProjectPageHeader = ({
    canDownloadBannerImage,
}: ProjectPageHeaderProps) => {
    const project = useProjectPreviewRouteData();
    const { desktop } = useAppDisplayModeContext();
    const bannerSize = getImagesSizes({
        desktop: FULL_COLUMN_WIDTH_FULL_SCREEN,
        mobile: BANNER_WIDTH_FULL_MOBILE,
    });
    const bannerDimensions = getBannerDimensions('project');

    return (
        <>
            {desktop ? (
                <>
                    {project.Banner && (
                        <div className="w-full md:px-12 md:pt-60">
                            <div className="md:mb-7 md:pr-[18.25rem]">
                                <h1 className="my-0 line-clamp-2 font-display text-[9.8125rem] font-semibold leading-[95%] tracking-[-0.19625rem] text-gray-900 md:pb-[0.05em]">
                                    {project.Title}
                                </h1>
                            </div>
                            <DetailBannerSection
                                {...project.Banner}
                                title={project.Title}
                                dimensions={bannerDimensions}
                                size={bannerSize}
                                isVideoBanner={false}
                                videoBannerUrl={
                                    project.Banner.videoBannerUrl ?? ''
                                }
                                showBackButton={false}
                                awards={project.Awards}
                                showOverlay={desktop}
                                actions={[
                                    <DownloadAction
                                        key="downloadAction"
                                        canDownload={canDownloadBannerImage}
                                        downloadLink={
                                            project.Banner.downloadLink ?? ''
                                        }
                                        libraryImageID={
                                            project.Banner.libraryImage?.ID ?? 0
                                        }
                                    />,
                                ]}
                            />
                        </div>
                    )}
                    <ProjectDetailsAccordion projects={project} />
                </>
            ) : (
                <>
                    {project.Banner && (
                        <DetailBannerSection
                            {...project.Banner}
                            title={project.Title}
                            dimensions={bannerDimensions}
                            size={bannerSize}
                            isVideoBanner={false}
                            videoBannerUrl={project.Banner.videoBannerUrl ?? ''}
                            showBackButton={false}
                            awards={project.Awards}
                            showOverlay={desktop}
                            actions={[
                                <DownloadAction
                                    key="downloadAction"
                                    canDownload={canDownloadBannerImage}
                                    downloadLink={
                                        project.Banner.downloadLink ?? ''
                                    }
                                    libraryImageID={
                                        project.Banner.libraryImage?.ID ?? 0
                                    }
                                />,
                            ]}
                        />
                    )}
                    <PageHeader data={project} />
                    {project.Content && <Summary data={project} />}
                    <DetailsMobile data={project} />
                    <ContactsMobile project={project} />
                    {project.Awards.length !== 0 && (
                        <Awards awards={project.Awards} />
                    )}
                </>
            )}
        </>
    );
};

export default ProjectPageHeader;
